/**
 * ActivityBlock — the event timeline + comment form, extracted from
 * WorkItemDrawer (UX-11 PR-2). Stage-change events store the stage SLUG
 * in body ("Moved to paperwork_sent"); the LABEL renders from
 * metadata.to_stage via stageLabelByKey (#7).
 *
 * The gold "Post" button stays hand-rolled: the warm-400 accent is the
 * drawer's established submit treatment and the Button primitive has no
 * gold variant — this file carries a conscious raw-button allowlist
 * entry (the lint's documented escape hatch) rather than a restyle.
 *
 * Design pass 2026-07-25 (soak r2): the stream is TRUNCATED to the most
 * recent few events, with "Show all" beneath. An intake that has been
 * worked for six weeks carries dozens of events, and rendering every one
 * of them by default put the drawer's longest section at its very
 * bottom, where it did nothing but add scroll. Recency is what gets
 * read; the history is one click away and nothing is hidden — the
 * affordance says how many there are. The comment box never folds.
 */

import { useState, type ReactNode } from 'react';
import type { WorkItemDetail } from '@/lib/api';
import type { useAddComment, useDeleteEvent, useEditEvent, useSetEventEnteredOn } from '@/lib/workspace';
import { shortDate, todayStr } from '@/components/workspace/workspaceFormat';
import { Button } from '@/components/primitives/Button';
import { TextAction, DestructiveTextAction } from '@/components/workspace/TextAction';
import { DrawerSection } from './drawerLayout';
import { DateInput } from '@/components/primitives/DateInput';

/** How many events show before "Show all". Enough to answer "what just
 *  happened here?" without becoming the page. */
const ACTIVITY_PREVIEW = 3;

export interface ActivityBlockProps {
  detail: WorkItemDetail;
  canManage: boolean;
  addComment: ReturnType<typeof useAddComment>;
  stageLabelByKey: Map<string, string>;
  /** The viewer: their own notes carry Edit and Delete. */
  currentUserId?: string | null;
  /** moderate_work_item_notes: Edit and Delete on anyone's note. */
  canModerateNotes?: boolean;
  editEvent?: ReturnType<typeof useEditEvent>;
  deleteEvent?: ReturnType<typeof useDeleteEvent>;
  /** Correct the day a card entered a stage (a stage_change event). */
  setEnteredOn?: ReturnType<typeof useSetEventEnteredOn>;
}

/** The day a stage_change event says the card entered its stage: the
 *  office's correction (metadata.entered_on) when one was made, else the
 *  auto stamp. Both read as YYYY-MM-DD. */
export function stageEnteredOn(ev: Pick<WorkItemDetail['events'][number], 'metadata' | 'created_at'>): string {
  const corrected = (ev.metadata as { entered_on?: unknown } | null)?.entered_on;
  return typeof corrected === 'string' && corrected.length >= 10 ? corrected.slice(0, 10) : ev.created_at.slice(0, 10);
}

/** Field pack v1: whole days between two ISO timestamps (for the time-in-stage
 *  annotation on stage moves). Pure; floors so "later the same day" reads 0. */
export function wholeDaysBetween(fromIso: string, toIso: string): number {
  const ms = new Date(toIso).getTime() - new Date(fromIso).getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
}

export function ActivityBlock({
  detail,
  canManage,
  addComment,
  stageLabelByKey,
  currentUserId = null,
  canModerateNotes = false,
  editEvent,
  deleteEvent,
  setEnteredOn,
}: ActivityBlockProps): ReactNode {
  const [comment, setComment] = useState('');
  const [showAll, setShowAll] = useState(false);
  // One note at a time is being edited or asked about; the id says which.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  // The stage_change event whose entered-on date is being corrected.
  const [editingEnteredOnId, setEditingEnteredOnId] = useState<string | null>(null);

  // A note (a 'comment' someone typed) carries Edit and Delete for its
  // author and for a moderating seat. Server kinds never do: they are the
  // record of what the system did.
  const canTouchNote = (ev: WorkItemDetail['events'][number]): boolean =>
    canManage && ev.kind === 'comment' && !!editEvent && !!deleteEvent
    && (canModerateNotes || (currentUserId !== null && ev.actor_user_id === currentUserId));

  // Field pack v1 (time between stages): each stage-change event is annotated
  // with how long the card sat in the stage it LEFT — derived from the gap to
  // the previous stage-change (or the card's creation, for the first move).
  // Display-only; nothing new is stored.
  // The day a move happened is the corrected entered_on when the office set
  // one, else the auto stamp; the reading below follows the corrected days.
  const stageMovesAsc = detail.events
    .filter((ev) => ev.kind === 'stage_change')
    .sort((a, b) => stageEnteredOn(a).localeCompare(stageEnteredOn(b)) || a.created_at.localeCompare(b.created_at));
  const daysInStageByEventId = new Map<string, { days: number; fromLabel: string | null }>();
  for (let i = 0; i < stageMovesAsc.length; i++) {
    const ev = stageMovesAsc[i]!;
    const prevOn = i > 0 ? stageEnteredOn(stageMovesAsc[i - 1]!) : detail.created_at.slice(0, 10);
    const fromKey = (ev.metadata as { from_stage?: string } | null)?.from_stage ?? null;
    daysInStageByEventId.set(ev.id, {
      days: wholeDaysBetween(prevOn, stageEnteredOn(ev)),
      fromLabel: fromKey ? (stageLabelByKey.get(fromKey) ?? fromKey) : null,
    });
  }

  // Newest first, sorted HERE as well as on the wire: the rail is the last
  // thing between the payload and the eye, so a cache merge or a payload
  // change can never flip it back to oldest-first. The preview is the head
  // of that list, so "Show all" only ever ADDS older history beneath.
  const eventsDesc = [...detail.events].sort((a, b) => b.created_at.localeCompare(a.created_at));
  const hidden = Math.max(0, eventsDesc.length - ACTIVITY_PREVIEW);
  const shown = showAll ? eventsDesc : eventsDesc.slice(0, ACTIVITY_PREVIEW);

  return (
    <DrawerSection
      title="Activity"
      testId="drawer-activity"
      meta={detail.events.length > 0 ? detail.events.length : undefined}
    >
      <div className="relative pl-[18px]">
        <span className="absolute left-[5px] top-1 bottom-4 w-0.5 bg-warm-200 block" aria-hidden="true" />
        {shown.map((ev) => {
          // #7: stage-change events store the stage SLUG in body ("Moved to
          // paperwork_sent"); render the stage LABEL from metadata.to_stage.
          const toKey = ev.kind === 'stage_change' ? (ev.metadata as { to_stage?: string } | null)?.to_stage : undefined;
          const text = toKey ? `Moved to ${stageLabelByKey.get(toKey) ?? toKey}` : (ev.body ?? ev.kind);
          // Time-in-stage: "· 5d in Screening" on the move that left it.
          const inStage = ev.kind === 'stage_change' ? daysInStageByEventId.get(ev.id) : undefined;
          const inStageText = inStage?.fromLabel ? ` · ${inStage.days}d in ${inStage.fromLabel}` : '';
          const touchable = canTouchNote(ev);
          const editing = editingId === ev.id;
          const asking = confirmDeleteId === ev.id;
          // A typed comment is prose someone wrote; every other kind is the
          // record of what the system did. The two render differently so a
          // comment stands out from the moves and status lines around it.
          const isComment = ev.kind === 'comment';
          // The date segment: a stage_change's entered-on is click-to-edit
          // for managers (the office moves cards after the fact), capped at
          // today. Every other kind, comments included, reads created_at.
          const dateNode: ReactNode = ev.kind === 'stage_change' ? (
            canManage && setEnteredOn ? (
              editingEnteredOnId === ev.id ? (
                <DateInput
                  autoFocus
                  defaultValue={stageEnteredOn(ev)}
                  max={todayStr()}
                  aria-label="Entered on"
                  data-testid={`activity-entered-on-input-${ev.id}`}
                  onBlur={() => setEditingEnteredOnId(null)}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v || v > todayStr()) return;
                    setEnteredOn.mutate({ id: detail.id, eventId: ev.id, enteredOn: v });
                    setEditingEnteredOnId(null);
                  }}
                  size="compact"
                />
              ) : (
                <button
                  type="button"
                  data-testid={`activity-entered-on-${ev.id}`}
                  title="Set the day the card entered this stage"
                  onClick={() => setEditingEnteredOnId(ev.id)}
                  className="underline decoration-dotted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded"
                >
                  {shortDate(stageEnteredOn(ev))}
                </button>
              )
            ) : (
              shortDate(stageEnteredOn(ev))
            )
          ) : (
            shortDate(ev.created_at)
          );
          // Edit / Delete: only ever offered on a comment (canTouchNote
          // requires kind === 'comment'), so this row lives in the comment
          // bubble; a system line never carries it.
          const noteActions: ReactNode = (touchable || asking) && !editing ? (
            <div className="mt-1 flex flex-wrap items-center gap-x-1">
              {touchable && !asking && (
                <span className="inline-flex items-center gap-x-0.5">
                  <TextAction
                    className="!min-h-0 !px-1.5 !py-0 text-[0.71875rem]"
                    data-testid={`activity-edit-${ev.id}`}
                    onClick={() => { setConfirmDeleteId(null); setEditDraft(ev.body ?? ''); setEditingId(ev.id); }}
                  >
                    Edit
                  </TextAction>
                  <DestructiveTextAction
                    className="!min-h-0 !px-1.5 !py-0 text-[0.71875rem]"
                    data-testid={`activity-delete-${ev.id}`}
                    onClick={() => { setEditingId(null); setConfirmDeleteId(ev.id); }}
                  >
                    Delete
                  </DestructiveTextAction>
                </span>
              )}
              {asking && (
                <span className="inline-flex items-center gap-x-1" data-testid={`activity-delete-ask-${ev.id}`}>
                  Delete this note?
                  <DestructiveTextAction
                    className="!min-h-0 !px-1.5 !py-0 text-[0.71875rem]"
                    data-testid={`activity-delete-confirm-${ev.id}`}
                    disabled={!!deleteEvent?.isPending}
                    onClick={() => deleteEvent?.mutate({ id: detail.id, eventId: ev.id }, { onSuccess: () => setConfirmDeleteId(null) })}
                  >
                    Delete
                  </DestructiveTextAction>
                  <TextAction
                    className="!min-h-0 !px-1.5 !py-0 text-[0.71875rem]"
                    data-testid={`activity-delete-cancel-${ev.id}`}
                    onClick={() => setConfirmDeleteId(null)}
                  >
                    Keep
                  </TextAction>
                </span>
              )}
            </div>
          ) : null;
          return (
            <div key={ev.id} className="relative pb-4" data-testid="activity-event" data-activity-kind={ev.kind}>
              <span className="absolute -left-4 top-1 w-2.5 h-2.5 rounded-pill bg-warm-400 border-2 border-cream block" aria-hidden="true" />
              {editing ? (
                <form
                  className="flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const b = editDraft.trim();
                    if (!b || !editEvent) return;
                    editEvent.mutate({ id: detail.id, eventId: ev.id, body: b }, { onSuccess: () => setEditingId(null) });
                  }}
                >
                  <input
                    autoFocus
                    value={editDraft}
                    onChange={(e) => setEditDraft(e.target.value)}
                    aria-label="Edit note"
                    data-testid={`activity-edit-input-${ev.id}`}
                    className="flex-1 border border-sage-300 rounded-lg px-3 py-1.5 text-[0.8125rem] bg-paper"
                  />
                  <button
                    type="submit"
                    disabled={!!editEvent?.isPending || editDraft.trim() === ''}
                    data-testid={`activity-edit-save-${ev.id}`}
                    className="rounded-base border border-warm-400 bg-paper px-[14px] py-1 text-[0.8125rem] font-semibold text-sage-800 hover:bg-warm-50 disabled:opacity-50"
                  >
                    Save
                  </button>
                  <TextAction data-testid={`activity-edit-cancel-${ev.id}`} onClick={() => setEditingId(null)}>Cancel</TextAction>
                </form>
              ) : isComment ? (
                // A typed comment: the speech-bubble treatment (a bordered
                // card, author + time in the header, the body at body size)
                // so it stands out from the quiet system lines around it.
                <div className="rounded-base border border-sage-200 bg-paper px-3 py-2" data-testid="activity-comment">
                  <div className="flex flex-wrap items-center gap-x-1 text-label text-sage-400">
                    <span className="font-semibold text-ink">{ev.actor_name ?? 'Someone'}</span>
                    <span>{dateNode}</span>
                    {ev.edited_at && <span data-testid="activity-edited-mark">edited</span>}
                  </div>
                  <div className="mt-1 text-sm text-ink whitespace-pre-wrap">{text}</div>
                  {noteActions}
                </div>
              ) : (
                // A system kind (stage_change / status_change / assignment /
                // checklist): a quiet one-line entry in the muted label
                // style — what happened, who, and when, never dressed up as
                // prose someone wrote.
                <div className="text-label text-sage-400" data-testid="activity-system-line">
                  {text}
                  {ev.actor_name ? ` · ${ev.actor_name}` : ''}
                  {' · '}{dateNode}
                  {inStageText && <span data-testid="activity-days-in-stage">{inStageText}</span>}
                  {ev.edited_at && <span data-testid="activity-edited-mark"> · edited</span>}
                </div>
              )}
            </div>
          );
        })}
        {detail.events.length === 0 && <div className="text-sm text-ink-soft">As the card moves, you'll see each step here.</div>}
      </div>
      {hidden > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="!min-h-0 -ml-1.5 mb-1 !px-1.5 !py-0.5 text-[12.5px]"
          data-testid="drawer-activity-show-all"
          aria-expanded={showAll}
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll ? 'Show recent only' : `Show all (${detail.events.length})`}
        </Button>
      )}
      {canManage && (
        <form className="flex gap-2 mt-1.5"
          onSubmit={(e) => { e.preventDefault(); const b = comment.trim(); if (!b) return; addComment.mutate({ id: detail.id, body: b }, { onSuccess: () => setComment('') }); }}>
          <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a note or comment" aria-label="Add a comment" data-testid="comment-input"
            className="min-w-0 flex-1 border border-sage-300 rounded-lg px-3 py-2.5 text-[13px] bg-paper" />
          <button type="submit" disabled={addComment.isPending || comment.trim() === ''} data-testid="comment-submit"
            className="shrink-0 rounded-base border border-warm-400 bg-paper px-[14px] py-1 text-[13px] font-semibold text-sage-800 hover:bg-warm-50 disabled:opacity-50">Post</button>
        </form>
      )}
    </DrawerSection>
  );
}
